/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Heading, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address/dist/types/common/components/address-add';
import { IAddressList, IAddressListItem, IAddressListProps } from '@msdyn365-commerce-modules/address/dist/types/common/components/address-list';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address/dist/types/common/components/address-show';
import { IAccountManagementAddressViewProps } from '@msdyn365-commerce-modules/address/dist/types/modules/account-management-address/account-management-address';
import { IAccountManagementAddressConfig } from '@msdyn365-commerce-modules/address/dist/types/modules/account-management-address/account-management-address.props.autogenerated';

const AddressShow: React.FC<IAddressShowProps> = ({
    AddressDetail,
    items
}) => {

    return (
        <Node {...AddressDetail}>
            {items && items.map((item: IAddressShowItem) => {
                return (
                    <>
                        {item.description}
                    </>
                );
            })}
        </Node>
    );
};

const AddressList: React.FC<IAddressList> = ({
    List,
    isShowList,
    heading,
    items
}) => {

    if (!isShowList) {
        return null;
    }

    return (
        <Node {...List}>
            {heading}
            {items && items.map((item: IAddressListItem) => {
                const { Item, key, error, showItems, isShowPrimaryButton, primaryButton, editButton } = item;
                const { Error, isShowError, title, message } = error;
                return (<Node {...Item} key={key}>
                    <AddressShow  {...showItems} />
                    {isShowError && <Node {...Error}>
                        {title}
                        {message}
                    </Node>}
                    {isShowPrimaryButton && primaryButton}
                    {editButton}
                </Node>);
            })}
        </Node>
    );
};

const AddressLists: React.FC<IAddressListProps> = ({
    ListAddress,
    heading,
    isShowEmptyListMessage,
    emptyListMessage,
    addButton,
    primaryAddressList,
    otherAddressList
}) => {

    return (
        <Node {...ListAddress}>
            {heading}
            {isShowEmptyListMessage && emptyListMessage}
            {addButton}
            <AddressList {...primaryAddressList} />
            <AddressList {...otherAddressList} />
        </Node>
    );
};

const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
}) => {

    return (
        <Node {...AddressForm}>
            {heading}
            {items && items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                return (<Node {...AddressItem} key={key}>
                    {label}
                    {alert}
                    {input}
                </Node>);
            })}
            {hasError && <Node {...error.AddressError}>
                {error.title}
                {error.message}
            </Node>}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const AccountManagementAddressView: React.FC<IAccountManagementAddressViewProps> = props => {
    const { AccountAddressManagement, addUpdateAddress, viewState, showAddressList, showAddOrUpdateAddress } = props;

    // update heading in props if not provided from module
    if (!showAddOrUpdateAddress.heading) {
        // create heading component from configured values
        const addAddressHeading = (AccountAddressManagement.moduleProps.config as IAccountManagementAddressConfig).addAddressHeading;
        const editAddressHeading = (AccountAddressManagement.moduleProps.config as IAccountManagementAddressConfig).editAddressHeading;
        const headingText = addUpdateAddress.RecordId ? editAddressHeading : addAddressHeading;
        const heading = headingText && <Heading className='msc-address-form__heading' {...headingText} />;

        showAddOrUpdateAddress.heading = heading;
    }

    return (
        <Module {...AccountAddressManagement}>
            {viewState.isShowAddresList && <AddressLists {...showAddressList} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress}/>}
        </Module>
    );
};

export default AccountManagementAddressView;
